/* screens/broadcasts/BroadcastFormDrawer.jsx — сборка/редактирование рассылки.
   Центр: что/о чём/каналы/аудитория/дата/статистика. Дальше два таба (Telegram и
   Почта), у каждого свой контент и свой ЖИВОЙ предпросмотр (TG-пузырь / письмо в
   шаблоне). Превью и аудитория считаются по текущему контенту (live, без сохранения).
   Контролы — из дизайн-системы: Checkbox/Select/Segmented + конструктор кнопок. */
import React from 'react';
import { Drawer } from '../../components/ui/Drawer.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Field, TextInput, Textarea, Checkbox, Segmented } from '../../components/ui/Field.jsx';
import { Select } from '../../components/ui/Select.jsx';
import { Icon } from '../../components/ui/Icon.jsx';
import { Tabs } from '../../components/patterns/Tabs.jsx';
import { Spinner } from '../../components/ui/Spinner.jsx';
import { useToast } from '../../app/providers/ToastProvider.jsx';
import { useConfirm } from '../../app/providers/ConfirmProvider.jsx';
import * as api from '../../lib/api/marketing.js';

const { useState, useEffect, useRef, useMemo } = React;

const PRESETS = [
  { value: 'all_users', label: 'Все пользователи' },
  { value: 'active_paid', label: 'Есть активная платная подписка' },
  { value: 'no_paid', label: 'Без активной платной подписки' },
  { value: 'churned', label: 'Была платная, но не продлевают' },
  { value: 'specific_users', label: 'Конкретные пользователи' },
  { value: 'academy_course', label: 'Ученики курса (Академия)' },
  { value: 'academy_all', label: 'Ученики всех курсов (Академия)' },
];
const ACAD_STATUS = [
  { value: '', label: 'Все записанные' },
  { value: 'active', label: 'Активные' },
  { value: 'completed', label: 'Завершившие' },
  { value: 'expired', label: 'Истёк доступ' },
];
const MEDIA = [
  { value: 'none', label: 'Без медиа' },
  { value: 'photo', label: 'Фото', icon: 'image' },
  { value: 'video', label: 'Видео', icon: 'camera' },
];
const MAX_CTA = 2;

// Теги, которые Telegram принимает с parse_mode=HTML (зеркалит backend
// marketing/formatting.py TELEGRAM_ALLOWED_TAGS). Блочных (<p>/<div>/<br>) тут НЕТ.
const TG_ALLOWED = new Set(['b', 'strong', 'i', 'em', 'u', 'ins', 's', 'strike', 'del',
  'a', 'code', 'pre', 'blockquote', 'tg-spoiler', 'tg-emoji', 'span']);
const TG_ALLOWED_HINT = '<b>, <i>, <u>, <s>, <a>, <code>, <pre>, <blockquote>, <tg-spoiler>';
function unsupportedTgTags(html) {
  if (!html) return [];
  const out = [], seen = new Set();
  const re = /<\/?\s*([a-zA-Z][a-zA-Z0-9-]*)/g;
  let m;
  while ((m = re.exec(html))) {
    const t = m[1].toLowerCase();
    if (!TG_ALLOWED.has(t) && !seen.has(t)) { seen.add(t); out.push(t); }
  }
  return out;
}

function emptyForm() {
  return {
    title: '', internal_note: '', body_html: '', media_kind: 'none', media_file: null,
    disable_web_page_preview: true, primary_cta_label: '', primary_cta_url: '',
    secondary_cta_label: '', secondary_cta_url: '', send_telegram: true, send_email: true,
    email_subject: '', email_body_html: '', audience_preset: 'all_users',
    academy_course: '', academy_status: '', scheduled_for: '',
  };
}
function ctasFrom(b) {
  const out = [];
  if (b.primary_cta_label || b.primary_cta_url) out.push({ label: b.primary_cta_label || '', url: b.primary_cta_url || '' });
  if (b.secondary_cta_label || b.secondary_cta_url) out.push({ label: b.secondary_cta_label || '', url: b.secondary_cta_url || '' });
  return out;
}
function toLocalInput(iso) {
  if (!iso) return '';
  const d = new Date(iso); const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

function BroadcastFormDrawer({ broadcastId: initialId, onClose, onChanged }) {
  const toast = useToast();
  const confirm = useConfirm();
  const [id, setId] = useState(initialId || null);
  const [form, setForm] = useState(emptyForm());
  const [ctas, setCtas] = useState([]);
  const [existingMedia, setExistingMedia] = useState('');
  const [specUsers, setSpecUsers] = useState([]);
  const [loading, setLoading] = useState(!!initialId);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [meta, setMeta] = useState(null);
  const [tab, setTab] = useState('telegram');
  const [audience, setAudience] = useState(null);
  const [preview, setPreview] = useState(null);
  const [userQuery, setUserQuery] = useState('');
  const [userHits, setUserHits] = useState([]);
  const [acadCourses, setAcadCourses] = useState([]);

  const editable = !meta || ['draft', 'scheduled'].includes(meta.status);
  const set = (k) => (v) => { setForm((f) => ({ ...f, [k]: v })); setErrors({}); };
  const contentRef = useRef(form);
  contentRef.current = form;

  // кнопки ⇄ поля формы (конструктор держит массив, форма — 2 слота для бэка)
  const applyCtas = (list) => {
    setCtas(list);
    setForm((f) => ({ ...f,
      primary_cta_label: list[0]?.label || '', primary_cta_url: list[0]?.url || '',
      secondary_cta_label: list[1]?.label || '', secondary_cta_url: list[1]?.url || '' }));
    setErrors({});
  };
  const updateCta = (i, key, val) => applyCtas(ctas.map((c, j) => (j === i ? { ...c, [key]: val } : c)));
  const addCta = () => applyCtas([...ctas, { label: '', url: '' }]);
  const removeCta = (i) => applyCtas(ctas.filter((_, j) => j !== i));

  const setMediaKind = (v) => {
    setForm((f) => ({ ...f, media_kind: v, media_file: v === 'none' ? null : f.media_file }));
    setErrors({});
  };
  const pickFile = (file) => { setForm((f) => ({ ...f, media_file: file })); setExistingMedia(''); setErrors({}); };
  const clearMedia = () => { setForm((f) => ({ ...f, media_file: null, media_kind: 'none' })); setExistingMedia(''); setErrors({}); };

  // Живой контроль тегов Telegram (мгновенно, без раунд-трипа на сервер).
  const tgBadTags = useMemo(
    () => (form.send_telegram ? unsupportedTgTags(form.body_html) : []),
    [form.body_html, form.send_telegram]);

  const mediaPreviewUrl = useMemo(
    () => (form.media_file instanceof File && form.media_kind === 'photo' ? URL.createObjectURL(form.media_file) : ''),
    [form.media_file, form.media_kind]);
  useEffect(() => () => { if (mediaPreviewUrl) URL.revokeObjectURL(mediaPreviewUrl); }, [mediaPreviewUrl]);

  // загрузка существующей
  useEffect(() => {
    if (!initialId) return;
    (async () => {
      try {
        const b = await api.getBroadcast(initialId);
        setForm({ ...emptyForm(), ...Object.fromEntries(Object.keys(emptyForm()).map((k) => [k, b[k] ?? emptyForm()[k]])), media_file: null, scheduled_for: toLocalInput(b.scheduled_for) });
        setCtas(ctasFrom(b)); setExistingMedia(b.media_url || '');
        setSpecUsers(b.specific_users_detail || []); setMeta(b);
      } finally { setLoading(false); }
    })();
  }, [initialId]);

  // ЖИВОЕ превью (по контенту, debounce)
  useEffect(() => {
    const t = setTimeout(async () => {
      const f = contentRef.current;
      try {
        setPreview(await api.getPreview({
          body_html: f.body_html, email_subject: f.email_subject, email_body_html: f.email_body_html,
          media_kind: f.media_kind, primary_cta_label: f.primary_cta_label, primary_cta_url: f.primary_cta_url,
          secondary_cta_label: f.secondary_cta_label, secondary_cta_url: f.secondary_cta_url,
          send_telegram: f.send_telegram, send_email: f.send_email,
        }));
      } catch { /* noop */ }
    }, 400);
    return () => clearTimeout(t);
  }, [form.body_html, form.email_body_html, form.email_subject, form.media_kind,
      form.primary_cta_label, form.primary_cta_url, form.secondary_cta_label,
      form.secondary_cta_url, form.send_telegram, form.send_email]);

  // ЖИВАЯ аудитория (по пресету/каналам/конкретным/курсу)
  useEffect(() => {
    const t = setTimeout(async () => {
      try {
        setAudience(await api.getAudience({
          audience_preset: form.audience_preset, send_telegram: form.send_telegram,
          send_email: form.send_email, specific_users: specUsers.map((u) => u.id),
          academy_course: form.audience_preset === 'academy_course' ? (form.academy_course || null) : null,
          academy_status: form.academy_status || '',
        }));
      } catch { /* noop */ }
    }, 400);
    return () => clearTimeout(t);
  }, [form.audience_preset, form.send_telegram, form.send_email, specUsers,
      form.academy_course, form.academy_status]);

  // курсы Академии для выпадашки — грузим при выборе пресета «ученики курса»
  useEffect(() => {
    if (form.audience_preset === 'academy_course' && acadCourses.length === 0) {
      api.getAcademyCourses().then(setAcadCourses).catch(() => {});
    }
  }, [form.audience_preset, acadCourses.length]);

  // поиск пользователей
  useEffect(() => {
    if (form.audience_preset !== 'specific_users' || userQuery.trim().length < 2) { setUserHits([]); return; }
    let alive = true;
    const t = setTimeout(async () => { try { const r = await api.searchUsers(userQuery.trim()); if (alive) setUserHits(r); } catch { /* noop */ } }, 300);
    return () => { alive = false; clearTimeout(t); };
  }, [userQuery, form.audience_preset]);

  const buildBody = () => {
    const body = { ...form };
    body.scheduled_for = form.scheduled_for ? new Date(form.scheduled_for).toISOString() : null;
    if (!form.send_telegram) body.media_kind = 'none';   // медиа — только для Telegram
    if (!(form.media_file instanceof File)) delete body.media_file;  // нет нового файла → не трогаем существующий
    if (form.audience_preset === 'specific_users') body.specific_users = specUsers.map((u) => u.id);
    // '' → null (FK); у «всех курсов» курс не нужен — не отправляем выбранный ранее
    body.academy_course = form.audience_preset === 'academy_course' ? (form.academy_course || null) : null;
    return body;
  };

  // Валидация — зеркалит правила модели и СОСТЫКОВАНА С КАНАЛАМИ: проверяем контент
  // канала только если он включён. Возвращает {errs, tab} либо null если всё ок.
  const validate = () => {
    const errs = {};
    const hasMedia = form.media_file instanceof File || !!existingMedia;
    if (!form.title.trim()) errs.title = 'Укажите название рассылки.';
    if (!form.send_telegram && !form.send_email) errs._form = 'Выберите хотя бы один канал — Telegram или почту.';
    if (form.send_telegram) {
      if (!form.body_html.trim() && !hasMedia) errs.body_html = 'Для Telegram добавьте текст сообщения или медиа.';
      if (!errs.body_html && tgBadTags.length) errs.body_html = `Telegram не поддерживает теги: ${tgBadTags.map((t) => `<${t}>`).join(', ')}. Переносы строк — через Enter, без <br>/<p>/<div>.`;
      if (form.media_kind !== 'none' && !hasMedia) errs.media = 'Прикрепите файл для выбранного медиа.';
      ctas.forEach((c) => {
        if (c.label.trim() && !c.url.trim()) errs.cta = 'У кнопки заполните и ссылку.';
        if (!c.label.trim() && c.url.trim()) errs.cta = 'У кнопки заполните и текст.';
        if (c.url.trim() && !/^https?:\/\//i.test(c.url.trim())) errs.cta = 'Ссылка кнопки должна начинаться с http(s)://';
      });
    }
    if (form.send_email && !form.email_body_html.trim()) errs.email_body_html = 'Для письма нужно тело (HTML).';
    if (!Object.keys(errs).length) return null;
    const goTab = (errs.body_html || errs.media || errs.cta) ? 'telegram' : (errs.email_body_html ? 'email' : null);
    return { errs, tab: goTab };
  };

  const save = async () => {
    const bad = validate();
    if (bad) { setErrors(bad.errs); if (bad.tab) setTab(bad.tab); return; }
    setSaving(true); setErrors({});
    try {
      const saved = id ? await api.updateBroadcast(id, buildBody()) : await api.createBroadcast(buildBody());
      setId(saved.id); setMeta(saved); setExistingMedia(saved.media_url || existingMedia);
      toast.show('Рассылка сохранена', 'accent'); onChanged && onChanged();
    } catch (e) {
      // DRF-ошибки уровня поля приходят в e.message (+ e.field), без e.detail — показываем
      // КОНКРЕТНУЮ причину (напр. «лимит 1024, уберите ещё N») под нужным полем и в тосте,
      // а не общее «Проверьте поля».
      const msg = e?.message || e?.detail || 'Не удалось сохранить — проверьте поля.';
      const fieldMap = { media_file: 'media', primary_cta_url: 'cta', primary_cta_label: 'cta',
                         secondary_cta_url: 'cta', secondary_cta_label: 'cta', send_telegram: '_form' };
      const fld = e?.field ? (fieldMap[e.field] || e.field) : null;
      if (fld && fld !== '_form') { setErrors({ [fld]: msg }); setTab(fld === 'email_body_html' ? 'email' : 'telegram'); }
      else setErrors({ _form: msg });
      toast.show(msg, 'danger');
    } finally { setSaving(false); }
  };

  const test = async (channel) => {
    if (!id) { toast.show('Сначала сохраните рассылку', 'muted'); return; }
    try { const r = await api.sendTest(id, channel); toast.show(`Тест отправлен → ${r.to}`, 'success'); }
    catch (e) { toast.show(e?.detail || 'Ошибка теста', 'danger'); }
  };
  const sendNow = async () => {
    if (!id) return;
    const bad = validate();
    if (bad) { setErrors(bad.errs); if (bad.tab) setTab(bad.tab); toast.show('Заполните обязательные поля', 'danger'); return; }
    if (!(await confirm({ title: 'Отправить сейчас?', body: `Рассылка уйдёт ${audience ? audience.targets : ''} получателям.`, confirmText: 'Отправить' }))) return;
    try { const r = await api.sendNow(id); toast.show(`Поставлено ${r.planned_recipients} — отправка началась`, 'accent'); const b = await api.getBroadcast(id); setMeta(b); onChanged && onChanged(); }
    catch (e) { toast.show(e?.detail || 'Не удалось запустить', 'danger'); }
  };
  const cancel = async () => {
    if (!id) return;
    if (!(await confirm({ title: 'Остановить рассылку?', confirmText: 'Остановить', tone: 'danger' }))) return;
    try { await api.cancelBroadcast(id); const b = await api.getBroadcast(id); setMeta(b); onChanged && onChanged(); toast.show('Остановлена', 'muted'); }
    catch (e) { toast.show(e?.detail || 'Ошибка', 'danger'); }
  };

  const footer = (
    <>
      <Button variant="ghost" onClick={onClose} disabled={saving}>Закрыть</Button>
      <div className="grow" />
      {editable && <Button variant="primary" loading={saving} onClick={save}>Сохранить</Button>}
    </>
  );

  if (loading) return <Drawer open onClose={onClose} size="lg" title="Рассылка"><div className="center pad-24"><Spinner /></div></Drawer>;

  const tgPrev = preview?.telegram;
  const emPrev = preview?.email;
  const accept = form.media_kind === 'photo' ? 'image/*' : 'video/*';

  return (
    <Drawer open onClose={onClose} size="lg" title={id ? form.title || 'Рассылка' : 'Новая рассылка'} footer={footer}>
      <div className="bcast-form stack stack-12">
        {/* ── Центр: что/о чём/статистика/каналы/аудитория/дата ── */}
        {meta && (
          <div className="bcast-status">
            <span className={'bcast-badge bcast-badge--' + meta.status}>{meta.status_display}</span>
            {meta.status !== 'draft' && (
              <span className="bcast-stats t-caption">получателей {meta.planned_recipients} · доставлено {meta.sent_count} · ошибок {meta.failed_count}{meta.blocked_count ? ` · заблок. ${meta.blocked_count}` : ''} · {meta.progress}%</span>
            )}
          </div>
        )}
        {errors._form && <div className="bcast-error"><Icon name="alert-circle" size={14} /> {errors._form}</div>}

        <fieldset className="bcast-fs" disabled={!editable}>
          <Field label="Название рассылки" required error={errors.title}>
            <TextInput value={form.title} invalid={!!errors.title} onChange={(e) => set('title')(e.target.value)} placeholder="Внутреннее название" />
          </Field>
          <Field label="О чём рассылка" hint="Заметка для команды — в сообщения не попадает.">
            <Textarea value={form.internal_note} onChange={(e) => set('internal_note')(e.target.value)} minRows={2} />
          </Field>
          <Field label="Куда отправляем" hint="По умолчанию — на оба канала. Обязательные поля проверяются по включённым каналам.">
            <div className="bcast-channels">
              <div className={'bcast-chan' + (form.send_telegram ? ' is-on' : '')}>
                <Checkbox label="Telegram" checked={form.send_telegram} onChange={(e) => set('send_telegram')(e.target.checked)} />
              </div>
              <div className={'bcast-chan' + (form.send_email ? ' is-on' : '')}>
                <Checkbox label="Почта" checked={form.send_email} onChange={(e) => set('send_email')(e.target.checked)} />
              </div>
            </div>
          </Field>
          <Field label="Аудитория">
            <Select value={form.audience_preset} onChange={set('audience_preset')} options={PRESETS} searchable={false} ariaLabel="Аудитория" />
          </Field>
          {(form.audience_preset === 'academy_course' || form.audience_preset === 'academy_all') && (
            <div className="bcast-spec" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {form.audience_preset === 'academy_course' && <Field label="Курс" className="grow">
                <Select value={form.academy_course ? String(form.academy_course) : ''}
                  onChange={set('academy_course')} searchable={acadCourses.length > 8}
                  ariaLabel="Курс Академии"
                  options={[{ value: '', label: '— выберите курс —' },
                    ...acadCourses.map((c) => ({ value: String(c.id), label: `${c.title} (${c.enrollment_count})` }))]} />
              </Field>}
              <Field label="Статус">
                <Select value={form.academy_status} onChange={set('academy_status')}
                  searchable={false} ariaLabel="Статус ученика" options={ACAD_STATUS} />
              </Field>
            </div>
          )}
          {form.audience_preset === 'specific_users' && (
            <div className="bcast-spec">
              <TextInput value={userQuery} onChange={(e) => setUserQuery(e.target.value)} placeholder="Поиск по email/имени…" />
              {userHits.length > 0 && (
                <div className="bcast-hits">{userHits.map((u) => (
                  <button type="button" key={u.id} className="bcast-hit" onClick={() => { if (!specUsers.some((s) => s.id === u.id)) setSpecUsers((p) => [...p, u]); setUserQuery(''); setUserHits([]); }}>{u.full_name || u.email} <span className="t-caption">{u.email}</span></button>))}</div>
              )}
              <div className="bcast-chips">{specUsers.map((u) => (<span key={u.id} className="bcast-chip">{u.full_name || u.email}<button type="button" onClick={() => setSpecUsers((p) => p.filter((s) => s.id !== u.id))}><Icon name="x-circle" size={14} /></button></span>))}</div>
            </div>
          )}
          <Field label="Плановая дата запуска (необязательно)" hint="Пусто — рассылка останется черновиком до «Отправить сейчас».">
            <TextInput type="datetime-local" value={form.scheduled_for} onChange={(e) => set('scheduled_for')(e.target.value)} />
          </Field>
        </fieldset>

        {audience && (
          <div className="bcast-audience"><b>{audience.recipients}</b> получателей · в Telegram <b>{audience.telegram}</b> · на почту <b>{audience.email}</b> (всего отправок {audience.targets})</div>
        )}

        {/* ── Табы: контент и превью по каналам ── */}
        <Tabs variant="pills" value={tab} onChange={setTab}
          items={[{ value: 'telegram', label: 'Telegram' + (form.send_telegram ? '' : ' (выкл.)') },
                  { value: 'email', label: 'Почта' + (form.send_email ? '' : ' (выкл.)') }]} />

        {tab === 'telegram' && (
          <div className="stack stack-8">
            <fieldset className="bcast-fs" disabled={!editable}>
              <Field label="Текст сообщения (Telegram)" error={errors.body_html} hint="HTML Telegram: <b>, <i>, <u>, <s>, <a href>, <code>.">
                <Textarea value={form.body_html} invalid={!!errors.body_html || tgBadTags.length > 0} onChange={(e) => set('body_html')(e.target.value)} minRows={4} />
                {tgBadTags.length > 0 && (
                  <div className="bcast-tag-warn" role="alert">
                    <Icon name="alert-triangle" size={14} />
                    <span>Telegram не поймёт теги: <b>{tgBadTags.map((t) => `<${t}>`).join(', ')}</b>. Разрешены {TG_ALLOWED_HINT}. Переносы строк — просто Enter (не {'<br>/<p>/<div>'}). Сохранить не получится, пока не уберёте.</span>
                  </div>
                )}
              </Field>

              <Field label="Медиа" error={errors.media}>
                <Segmented options={MEDIA} value={form.media_kind} onChange={setMediaKind} ariaLabel="Тип медиа" />
                {form.media_kind !== 'none' && (
                  <div className="bcast-media">
                    {(form.media_file instanceof File) || existingMedia ? (
                      <div className="bcast-media__file">
                        {(mediaPreviewUrl || (existingMedia && form.media_kind === 'photo'))
                          ? <img className="bcast-media__thumb" src={mediaPreviewUrl || existingMedia} alt="" />
                          : <span className="bcast-media__thumb bcast-media__thumb--ph"><Icon name="camera" size={18} /></span>}
                        <span className="bcast-media__name truncate">{form.media_file instanceof File ? form.media_file.name : 'Текущий файл'}</span>
                        <label className="bcast-media__replace">Заменить<input type="file" accept={accept} hidden onChange={(e) => pickFile(e.target.files?.[0] || null)} /></label>
                        <Button iconOnly="trash" variant="ghost" size="sm" onClick={clearMedia} aria-label="Убрать медиа" />
                      </div>
                    ) : (
                      <label className={'bcast-media__drop' + (errors.media ? ' is-invalid' : '')}>
                        <Icon name="upload" size={20} />
                        <span>Выбрать {form.media_kind === 'photo' ? 'фото' : 'видео'} — нажмите или перетащите</span>
                        <input type="file" accept={accept} hidden onChange={(e) => pickFile(e.target.files?.[0] || null)} />
                      </label>
                    )}
                  </div>
                )}
              </Field>

              {/* конструктор кнопок */}
              <Field label="Кнопки под сообщением" error={errors.cta} hint={`Кнопки-ссылки в Telegram — до ${MAX_CTA}.`}>
                <div className="bcast-cta">
                  {ctas.map((b, i) => (
                    <div className="bcast-cta__row" key={i}>
                      <span className="bcast-cta__idx">{i + 1}</span>
                      <TextInput className="bcast-cta__label" value={b.label} placeholder="Текст кнопки" onChange={(e) => updateCta(i, 'label', e.target.value)} />
                      <div className="bcast-cta__url"><Icon name="link" size={14} /><TextInput value={b.url} placeholder="https://app.restorium.su" onChange={(e) => updateCta(i, 'url', e.target.value)} /></div>
                      <Button iconOnly="trash" variant="ghost" size="sm" onClick={() => removeCta(i)} aria-label="Удалить кнопку" />
                    </div>
                  ))}
                  {ctas.length < MAX_CTA && (
                    <button type="button" className="bcast-cta__add" onClick={addCta}><Icon name="plus" size={15} /> Добавить кнопку</button>
                  )}
                </div>
              </Field>
            </fieldset>

            <div className="bcast-prev__h">Предпросмотр Telegram</div>
            <div className="bcast-tg">
              {tgPrev?.media_kind && tgPrev.media_kind !== 'none' && (
                (mediaPreviewUrl || (existingMedia && tgPrev.media_kind === 'photo'))
                  ? <img className="bcast-tg__img" src={mediaPreviewUrl || existingMedia} alt="" />
                  : <div className="bcast-tg__media"><Icon name="image" size={14} /> {tgPrev.media_kind === 'photo' ? 'Фото' : 'Видео'}</div>)}
              <div className="bcast-tg__text" dangerouslySetInnerHTML={{ __html: tgPrev?.text || '<span style="color:#aaa">— текст появится здесь —</span>' }} />
              {(tgPrev?.buttons || []).map((b, i) => <a key={i} className="bcast-tg__btn" href={b.url} target="_blank" rel="noreferrer">{b.label}</a>)}
            </div>
          </div>
        )}

        {tab === 'email' && (
          <div className="stack stack-8">
            <fieldset className="bcast-fs" disabled={!editable}>
              <Field label="Тема письма" hint="Пусто → берётся название рассылки.">
                <TextInput value={form.email_subject} onChange={(e) => set('email_subject')(e.target.value)} />
              </Field>
              <Field label="Текст письма (HTML)" required error={errors.email_body_html} hint="Тело письма — шапка «Ресториум» и футер добавляются автоматически.">
                <Textarea value={form.email_body_html} invalid={!!errors.email_body_html} onChange={(e) => set('email_body_html')(e.target.value)} minRows={5} />
                {!form.email_body_html && form.body_html && (
                  <button type="button" className="bcast-copy" onClick={() => set('email_body_html')(form.body_html)}>Скопировать текст из Telegram</button>
                )}
              </Field>
            </fieldset>
            <div className="bcast-prev__h">Предпросмотр письма {emPrev?.subject ? `· тема: ${emPrev.subject}` : ''}</div>
            {emPrev?.html
              ? <iframe title="email" className="bcast-email" srcDoc={emPrev.html} />
              : <div className="bcast-email bcast-email--empty">— добавьте текст письма —</div>}
          </div>
        )}

        {id && (
          <div className="bcast-actions cluster cluster-8">
            <Button variant="secondary" size="sm" onClick={() => test('telegram')}>Тест в Telegram</Button>
            <Button variant="secondary" size="sm" onClick={() => test('email')}>Тест на почту</Button>
            <div className="grow" />
            {meta && !meta.is_terminal && meta.status !== 'processing' && <Button variant="primary" size="sm" onClick={sendNow}>Отправить сейчас</Button>}
            {meta && (meta.status === 'processing' || meta.status === 'scheduled') && <Button variant="danger" size="sm" onClick={cancel}>Остановить</Button>}
          </div>
        )}
      </div>
    </Drawer>
  );
}

export { BroadcastFormDrawer };
